import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { Dialog } from "@core/material";
import { GridsterConfig, GridsterItem } from "angular-gridster2";
import { Subject } from "rxjs";
import { editModeCheck } from "../../report-view/report-state/advanced-report.state";
import { ReportTableEditFormDialog, ReportTableEditFormDialogData } from "../../report-dialogs/report-table-edit-form/report-table-edit.form.dialog";
import { ReportComponentType } from '@core/data/types/advanced-report';
import { TableCompiledData } from '@core/data/types/Reports/report-tables';

@Component({
    selector: 'pv-report-table',
    changeDetection: ChangeDetectionStrategy.Default,
    preserveWhitespaces: false,
    templateUrl: 'report-table-component.html',
    styleUrls: ['report-table-component.scss'],
})
export class ReportTableComponent implements OnChanges, OnDestroy {

    @Input()
    tableControl: FormControl;

    @Input()
    configControl: FormControl;

    @Input()
    item: GridsterItem;

    @Input()
    editMode: boolean

    @Input()
    optionKey: string;

    @Input()
    reportKey: string;

    @Input()
    ownerOrgKey: string;

    @Input()
    gridsterConfig: GridsterConfig;

    @Input()
    configFormGroup: FormGroup;

    @Input()
    compiledData: TableCompiledData;

    @Output()
    delete = new EventEmitter<boolean>();

    @Output()
    duplicate = new EventEmitter<ReportComponentType>();

    @Output()
    editTypography = new EventEmitter<void>();

    layerIndex = 1;

    get isUnset(): boolean {
        const v = this.tableControl?.value;
        if (!v || !v.type) return true;
        // A new table ships with type STATIC but no content, so an empty table counts as unset too.
        if (v.type === 'STATIC')  return !(v.groups && v.groups.length);
        if (v.type === 'DYNAMIC') return !(v.dataPoints && v.dataPoints.length);
        return false;
    }

    private _destroy$ = new Subject();

    constructor(
        private _dialog: Dialog,
    ) {}

    ngOnChanges() {
        this.layerIndex = this.item?.layerIndex ? this.item.layerIndex : 1;
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    moveToTop() {
        if (!editModeCheck(this.editMode)) return;

        this.configFormGroup.patchValue({
            layerIndex: this.gridsterConfig.maxLayerIndex,
        });
    }

    moveForward() {
        if (!editModeCheck(this.editMode)) return;

        this.configFormGroup.patchValue({
            layerIndex: this.gridsterConfig.baseLayerIndex,
        });
    }

    moveDown() {
        if (!editModeCheck(this.editMode)) return;

        this.configFormGroup.patchValue({
            layerIndex: -this.gridsterConfig.baseLayerIndex,
        });
    }

    moveToBottom() {
        if (!editModeCheck(this.editMode)) return;

        this.configFormGroup.patchValue({
            layerIndex: -this.gridsterConfig.maxLayerIndex,
        });
    }

    edit() {
        if (!editModeCheck(this.editMode)) return;

        // TODO add edit dialog
        const data: ReportTableEditFormDialogData = {
            reportKey: this.reportKey,
            ownerOrgKey: this.ownerOrgKey,
            optionKey: this.optionKey,
            data: this.tableControl.value,
        };

        this._dialog.open(ReportTableEditFormDialog, {
            data,
            panelClass: 'reporting-theme',
            width: '720px',
            maxWidth: '95vw',
            autoFocus: false,
        })
        .afterClosed()
        .subscribe((result) => {
            if (!result?.status) return;

            this.tableControl.setValue(result?.data);
            this.tableControl.markAsDirty();
        })

    }

    remove() {
        this._dialog.confirm(
            'Delete Component',
            `Are you sure you want to delete this component?`,
            'Delete',
            'Cancel',
            true
        )
        .afterClosed()
        .subscribe(result => {
            if (!result) return;

            this.delete.emit(true);
        });
    }

    makeDuplicate() {
        this.duplicate.emit(ReportComponentType.TABLE);
    }
}